// React
import { useState } from "react";
import { useNavigate } from "react-router-dom";

// MUI
import { CircularProgress } from "@mui/material";
import { Check as CheckIcon } from "@mui/icons-material";

// Components
import { AppointmentStatusModal } from "../AppointmentStatusModal";
import PanAmNote from "../Common/PanAmNote";

// Resources
import { bookAppointment } from "API/Resource/appointment";

// Libraries
import { useTranslation } from "react-i18next";

import { getTranslatedReason } from "../Common/getTranslatedReason";

const BookAppointmentModal = ({
  patient,
  client,
  closeModal,
  appointment,
  refreshAppointments,
  isMBS = false,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [openSuccessScreen, setOpenSuccessScreen] = useState(false);
  const [openAppointmentUnavailable, setOpenAppointmentUnavailable] =
    useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const appointmentReason =
    appointment.type &&
    getTranslatedReason(
      appointment.type,
      client.appointmentReasons,
      i18n.language
    );

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await bookAppointment(appointment);
      setOpenSuccessScreen(true);
    } catch (error) {
      const errorMessages = {
        APPOINTMENT_SLOT_UNAVAILABLE: t(
          "bookAppointmentModal.errorSlotUnavailable"
        ),
        ACTIVE_APPOINTMENT_EXISTS: t(
          "bookAppointmentModal.errorActiveAppointment"
        ),
        APPOINTMENT_TIME_PASSED: t("bookAppointmentModal.errorTimePassed"),
      };
      const defaultErrorMessage = t("bookAppointmentModal.errorDefault");

      const errorMessage = errorMessages[error.message] || defaultErrorMessage;
      setErrorMessage(errorMessage);
      setOpenAppointmentUnavailable(true);
    } finally {
      setLoading(false);
    }
  };

  const onAlreadyBooked = async () => {
    setLoading(true);
    await refreshAppointments();
    closeModal();
    setLoading(false);
  };

  const handleConfirmClose = () => {
    if (loading) {
      return;
    }
    closeModal();
  };

  const handleSuccessClose = () => {
    navigate(isMBS ? "/mbs/search" : "/");
  };

  return (
    <>
      {!openSuccessScreen && !openAppointmentUnavailable ? (
        <AppointmentStatusModal
          open
          onClose={handleConfirmClose}
          title={t("bookAppointmentModal.confirmBooking")}
          patient={patient}
          appointment={appointment}
          isMBS={isMBS}
          reasonText={appointmentReason}
          clinicMessage={
            <PanAmNote clinicId={appointment.clinicId} isMBS={isMBS} />
          }
          actions={[
            {
              label: t("buttons.cancel"),
              variant: "text",
              onClick: handleConfirmClose,
              disabled: loading,
            },
            {
              label: loading
                ? t("bookAppointmentModal.booking")
                : t("buttons.confirm"),
              variant: "contained",
              onClick: handleConfirm,
              disabled: loading,
              startIcon: loading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <CheckIcon />
              ),
            },
          ]}
        />
      ) : openSuccessScreen ? (
        <AppointmentStatusModal
          open
          onClose={handleSuccessClose}
          title={t("bookAppointmentModal.successMessage")}
          statusVariant="success"
          patient={patient}
          appointment={appointment}
          isMBS={isMBS}
          reasonText={appointmentReason}
          clinicMessage={
            <PanAmNote clinicId={appointment.clinicId} isMBS={isMBS} />
          }
        />
      ) : (
        <AppointmentStatusModal
          open
          onClose={onAlreadyBooked}
          title={t("errorDialog.title")}
          statusVariant="error"
          patient={patient}
          appointment={appointment}
          statusMessage={errorMessage}
          showAppointmentSummary={false}
          actions={[
            {
              label: t("bookAppointmentModal.refreshAppointments"),
              variant: "contained",
              onClick: onAlreadyBooked,
              disabled: loading,
              startIcon: loading ? (
                <CircularProgress size={18} color="inherit" />
              ) : null,
            },
          ]}
        />
      )}
    </>
  );
};

export default BookAppointmentModal;
